// Auto-generated: bilingual content for UserManualComponent
// Key = section id, value = { en: HTML string, he: HTML string }
export const USER_MANUAL_CONTENT: Record<string, { en: string; he: string }> = {
  'overview-body': {
    en: `<p>Yabi C-Portal is a comprehensive, multi-tenant management platform for organizations that need to control physical access, manage events, track personnel, and communicate with stakeholders — all from a single web-based interface.</p>
<p>The portal adapts to a variety of operational domains, including gated residential complexes, kindergartens, sports and culture centers, courses and training programs, nursing facilities, and time-attendance operations. Each domain is a module that can be activated independently based on your organization's license.</p>
<h3 class="manual-subtitle">Key Capabilities</h3>
<ul class="manual-list">
  <li><strong>Access Control</strong> — Issue and manage digital access passes for employees, visitors, and residents. Passes are delivered to the Keyera mobile app for secure QR-based entry.</li>
  <li><strong>Event Management</strong> — Create events, assign locations, issue ticketed or free passes, and monitor entry in real time.</li>
  <li><strong>Personnel Management</strong> — Manage employees and their access permissions across areas and time windows.</li>
  <li><strong>Vehicle Management</strong> — Register and control vehicle access with license plate tracking.</li>
  <li><strong>Messaging</strong> — Send targeted messages (SMS/push) to authorized persons, residents, parents, or students — manually or via rule-based automation.</li>
  <li><strong>Reporting &amp; Analytics</strong> — Export attendance, access, and operational data to Excel.</li>
  <li><strong>Multi-Tenant</strong> — Supports multiple client organizations under one platform, with full data isolation.</li>
  <li><strong>External API</strong> — Integrate third-party ticketing or HR systems via the Yabi External API.</li>
</ul>`,
    he: `<p>Yabi C-Portal היא פלטפורמת ניהול מקיפה לארגונים שצריכים לשלוט בגישה פיזית, לנהל אירועים, לעקוב אחר כוח אדם ולתקשר עם בעלי עניין — הכל ממשק אחד מבוסס-ווב.</p>
<p>הפורטל מותאם למגוון תחומי פעילות: מתחמי מגורים מגודרים, גני ילדים, מרכזי תרבות וספורט, קורסים והכשרות, מתקני סיעוד ומערכות נוכחות. כל תחום הוא מודול שניתן להפעיל בנפרד לפי רישיון הארגון.</p>
<h3 class="manual-subtitle">יכולות מרכזיות</h3>
<ul class="manual-list">
  <li><strong>בקרת כניסה</strong> — הנפקה וניהול של תעודות כניסה דיגיטליות לעובדים, מבקרים ודיירים. התעודות נשלחות לאפליקציית Keyera לסריקת QR מאובטחת.</li>
  <li><strong>ניהול אירועים</strong> — יצירת אירועים, שיוך מיקומים, הנפקת כרטיסים וניטור כניסות בזמן אמת.</li>
  <li><strong>ניהול כוח אדם</strong> — ניהול עובדים והרשאות הגישה שלהם לאזורים ולחלונות זמן.</li>
  <li><strong>ניהול רכבים</strong> — רישום ושליטה בגישת רכבים עם מעקב לוחיות רישוי.</li>
  <li><strong>הודעות</strong> — שליחת הודעות ממוקדות (SMS/Push) לאנשים מורשים, דיירים, הורים או תלמידים — ידנית או אוטומטית לפי כללים.</li>
  <li><strong>דוחות ואנליטיקה</strong> — ייצוא נתוני נוכחות, גישה ופעילות לאקסל.</li>
  <li><strong>מולטי-טנאנט</strong> — תמיכה במספר ארגוני לקוחות בפלטפורמה אחת עם בידוד נתונים מלא.</li>
  <li><strong>API חיצוני</strong> — שילוב מערכות כרטיסים או HR של צד שלישי דרך ה-Yabi External API.</li>
</ul>`,
  },

  'gs-intro': {
    en: `<p>This section covers the first steps you will take when accessing C-Portal for the first time.</p>`,
    he: `<p>סעיף זה מכסה את הצעדים הראשונים בעת כניסה ל-C-Portal בפעם הראשונה.</p>`,
  },

  'gs-login-body': {
    en: `<p>Access the portal by navigating to your organization's portal URL. If your organization uses multi-tenant routing, the URL will include your organization ID (e.g., <code>/org/123/auth/login</code>). Otherwise, navigate to <code>/auth/login</code>.</p>
<ol class="manual-steps">
  <li>Enter your registered <strong>email address</strong> and <strong>password</strong>.</li>
  <li>Click <strong>Log In</strong>. You will be redirected to your home page based on your assigned role.</li>
  <li>If you have forgotten your password, contact your system administrator to reset it.</li>
</ol>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>Tip:</strong> Your home page after login depends on your user role. Administrators are typically taken to the main dashboard.</span></div>`,
    he: `<p>גש לפורטל על-ידי ניווט לכתובת הפורטל של הארגון שלך. אם הארגון משתמש בניתוב רב-דיירים, הכתובת תכלול את מזהה הארגון (למשל <code>/org/123/auth/login</code>). אחרת, נווט ל-<code>/auth/login</code>.</p>
<ol class="manual-steps">
  <li>הכנס את <strong>כתובת הדואר האלקטרוני</strong> ו<strong>הסיסמה</strong> הרשומים שלך.</li>
  <li>לחץ על <strong>התחבר</strong>. תופנה לדף הבית שלך בהתאם לתפקידך.</li>
  <li>אם שכחת את הסיסמה, פנה למנהל המערכת לאיפוס.</li>
</ol>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>טיפ:</strong> דף הבית לאחר הכניסה תלוי בתפקיד המשתמש שלך. מנהלים מועברים בדרך כלל ללוח המחוונים הראשי.</span></div>`,
  },

  'gs-navigation-body': {
    en: `<p>The portal has a consistent layout across all modules.</p>
<ul class="manual-list">
  <li><strong>Top Navbar</strong> — Displays the current page title, language switcher, and user profile options.</li>
  <li><strong>Left Sidebar</strong> — The main navigation menu. Items correspond to activated modules. The sidebar can be collapsed to maximize workspace.</li>
  <li><strong>Main Content Area</strong> — The central area where page content, tables, forms, and charts are displayed.</li>
  <li><strong>Help Center</strong> — Accessible from the sidebar; contains FAQ, downloadable files, this User Manual, and the API Reference (admin only).</li>
</ul>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>Tip:</strong> Sidebar items are controlled by your user permissions. If you cannot see a menu item, contact your administrator.</span></div>`,
    he: `<p>לפורטל יש פריסה אחידה בכל המודולים.</p>
<ul class="manual-list">
  <li><strong>סרגל ניווט עליון</strong> — מציג את כותרת הדף הנוכחי, מחליף שפה ואפשרויות פרופיל משתמש.</li>
  <li><strong>סרגל צד שמאלי</strong> — תפריט הניווט הראשי. הפריטים מתאימים למודולים הפעילים. ניתן לכווץ את הסרגל להגדלת סביבת העבודה.</li>
  <li><strong>אזור תוכן ראשי</strong> — האזור המרכזי שבו מוצגים תוכן הדף, טבלאות, טפסים וגרפים.</li>
  <li><strong>מרכז עזרה</strong> — נגיש מסרגל הצד; מכיל שאלות נפוצות, קבצים להורדה, מדריך זה ו-API Reference (למנהלים בלבד).</li>
</ul>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>טיפ:</strong> פריטי סרגל הצד נשלטים על-ידי הרשאות המשתמש שלך. אם אינך רואה פריט תפריט, פנה למנהל.</span></div>`,
  },

  'gs-language-body': {
    en: `<p>The C-Portal supports both <strong>Hebrew</strong> (right-to-left) and <strong>English</strong> (left-to-right). The interface language can be switched at any time without logging out.</p>
<ol class="manual-steps">
  <li>Locate the language toggle in the top navigation bar.</li>
  <li>Click the flag or language code (HE / EN) to switch languages.</li>
  <li>The entire interface — including menus, labels, and dates — will update immediately.</li>
</ol>
<div class="manual-note"><i class="pi pi-exclamation-circle"></i><span><strong>Note:</strong> Date formats and text direction (RTL/LTR) automatically adjust when switching languages.</span></div>`,
    he: `<p>C-Portal תומך גם ב<strong>עברית</strong> (מימין לשמאל) וגם ב<strong>אנגלית</strong> (משמאל לימין). ניתן להחליף שפה בכל עת מבלי להתנתק.</p>
<ol class="manual-steps">
  <li>אתר את מתג השפה בסרגל הניווט העליון.</li>
  <li>לחץ על הדגל או קוד השפה (HE / EN) להחלפה.</li>
  <li>כל הממשק — כולל תפריטים, תוויות ותאריכים — יתעדכן מיד.</li>
</ol>
<div class="manual-note"><i class="pi pi-exclamation-circle"></i><span><strong>הערה:</strong> פורמטי תאריכים וכיוון הטקסט (RTL/LTR) מתכוונים אוטומטית בעת החלפת שפה.</span></div>`,
  },

  'access-control-body': {
    en: `<p>The Access Control module is the core of Yabi C-Portal. It enables organizations to manage who enters their premises — through digital passes delivered to the <strong>Keyera</strong> mobile app. This module covers authorized persons, vehicles, employees, and hardware management.</p>`,
    he: `<p>מודול בקרת הכניסה הוא ליבת Yabi C-Portal. הוא מאפשר לארגונים לנהל מי נכנס למתחם — דרך תעודות כניסה דיגיטליות המועברות לאפליקציית <strong>Keyera</strong>. המודול מכסה אנשים מורשים, רכבים, עובדים וניהול חומרה.</p>`,
  },

  'ac-dashboard-body': {
    en: `<p>The Access Control Dashboard provides a real-time view of all authorized individuals and activity in your facility.</p>
<ul class="manual-list">
  <li><strong>Grid View</strong> — Card-based grid showing each person's name, photo, and current access status.</li>
  <li><strong>List View</strong> — Tabular list with sortable columns for name, area, and expiry.</li>
  <li><strong>Graph Views</strong> — Visual charts showing entry patterns, peak times, and area occupancy.</li>
  <li><strong>KPI Bar</strong> — Summary statistics: total authorized, currently inside, entries today, and alerts.</li>
</ul>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>Tip:</strong> Use the filter controls to narrow the dashboard view by area, status, or date range. The dashboard updates in real time.</span></div>`,
    he: `<p>לוח המחוונים של בקרת הכניסה מספק תצוגה בזמן אמת של כל האנשים המורשים והפעילות במתקן.</p>
<ul class="manual-list">
  <li><strong>תצוגת רשת</strong> — רשת קלפים המציגה שם, תמונה וסטטוס גישה נוכחי של כל אדם.</li>
  <li><strong>תצוגת רשימה</strong> — רשימה טבלאית עם עמודות מסודרות לפי שם, אזור ותפוגה.</li>
  <li><strong>תצוגות גרף</strong> — גרפים חזותיים המציגים דפוסי כניסה, שעות שיא ותפוסת אזור.</li>
  <li><strong>שורת KPI</strong> — סיכום סטטיסטי: סך המורשים, נוכחים כעת, כניסות היום והתראות.</li>
</ul>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>טיפ:</strong> השתמש בפקדי הסינון לצמצום תצוגת לוח המחוונים לפי אזור, סטטוס או טווח תאריכים. הלוח מתעדכן בזמן אמת.</span></div>`,
  },

  'ac-passes-body': {
    en: `<p>Access Passes are digital permits issued to individuals, granting them entry to specific areas during specific time windows. Passes are delivered to the holder's <strong>Keyera</strong> app via push notification.</p>
<h3 class="manual-subtitle">Inviting a New Person</h3>
<ol class="manual-steps">
  <li>Navigate to <strong>Authorized &gt; Access Passes</strong>.</li>
  <li>Click the <strong>Invite</strong> button (top-right).</li>
  <li>Fill in: full name, national ID, mobile number, email, and allowed areas.</li>
  <li>Set time limitations if needed (valid from/to dates and hours).</li>
  <li>Choose the security level (0 = basic, 1 = standard, 2 = high security).</li>
  <li>Click <strong>Save</strong>. The pass is sent to the recipient's Keyera app.</li>
</ol>
<h3 class="manual-subtitle">Managing Existing Passes</h3>
<ul class="manual-list">
  <li><strong>Edit</strong> — Modify allowed areas, time limits, or security level.</li>
  <li><strong>Activate / Deactivate</strong> — Enable or disable a pass without deleting it.</li>
  <li><strong>Transfer</strong> — Pass holders can transfer their pass to another person via Keyera (if enabled).</li>
  <li><strong>Delete</strong> — Permanently remove a pass.</li>
  <li><strong>Bulk Actions</strong> — Select multiple passes for batch activation, deactivation, or export.</li>
</ul>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>Tip:</strong> Use Settings &gt; Uploads to bulk-import passes from an Excel file.</span></div>`,
    he: `<p>תעודות כניסה הן אישורים דיגיטליים הניתנים לאנשים, המעניקים להם כניסה לאזורים ספציפיים בחלונות זמן מוגדרים. התעודות נשלחות לאפליקציית <strong>Keyera</strong> של המחזיק כהתראת Push.</p>
<h3 class="manual-subtitle">הזמנת אדם חדש</h3>
<ol class="manual-steps">
  <li>נווט ל<strong>מורשים &gt; תעודות כניסה</strong>.</li>
  <li>לחץ על כפתור <strong>הזמנה</strong> (פינה ימנית עליונה).</li>
  <li>מלא: שם מלא, מספר זהות, טלפון, דואר אלקטרוני ואזורים מורשים.</li>
  <li>הגדר הגבלות זמן במידת הצורך (תאריכי תחילה/סיום ושעות).</li>
  <li>בחר רמת אבטחה (0 = בסיסי, 1 = רגיל, 2 = אבטחה גבוהה).</li>
  <li>לחץ <strong>שמור</strong>. התעודה נשלחת לאפליקציית Keyera של הנמען.</li>
</ol>
<h3 class="manual-subtitle">ניהול תעודות קיימות</h3>
<ul class="manual-list">
  <li><strong>עריכה</strong> — שינוי אזורים מורשים, הגבלות זמן או רמת אבטחה.</li>
  <li><strong>הפעלה / השבתה</strong> — הפעל או השבת תעודה מבלי למחוק אותה.</li>
  <li><strong>העברה</strong> — מחזיקי תעודה יכולים להעביר אותה לאדם אחר דרך Keyera (אם מאופשר).</li>
  <li><strong>מחיקה</strong> — הסרה קבועה של תעודה.</li>
  <li><strong>פעולות על-קבוצתיות</strong> — בחר מספר תעודות להפעלה, השבתה או ייצוא בצובר.</li>
</ul>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>טיפ:</strong> השתמש בהגדרות &gt; העלאות לייבוא תעודות בצובר מקובץ אקסל.</span></div>`,
  },

  'ac-vehicles-body': {
    en: `<p>The Vehicles module enables your organization to manage vehicle access to parking areas and gated facilities.</p>
<ul class="manual-list">
  <li><strong>Register Vehicle</strong> — Add a vehicle with license plate, owner name, make/model, and allowed parking areas.</li>
  <li><strong>Vehicle Pass</strong> — Issue a digital vehicle pass to the owner's Keyera app, enabling remote gate opening.</li>
  <li><strong>Activate / Deactivate</strong> — Control vehicle access without removing the record.</li>
  <li><strong>Search &amp; Filter</strong> — Search by license plate, owner name, or status.</li>
  <li><strong>Export</strong> — Export the vehicle list to Excel for auditing.</li>
</ul>
<div class="manual-note"><i class="pi pi-exclamation-circle"></i><span><strong>Note:</strong> Vehicle management requires the <em>Vehicles</em> permission. Contact your administrator if this section is not visible.</span></div>`,
    he: `<p>מודול הרכבים מאפשר לארגון לנהל גישת רכבים לחניונים ומתחמים מגודרים.</p>
<ul class="manual-list">
  <li><strong>רישום רכב</strong> — הוסף רכב עם לוחית רישוי, שם בעלים, יצרן/דגם ואזורי חניה מורשים.</li>
  <li><strong>תעודת רכב</strong> — הנפק תעודת רכב דיגיטלית לאפליקציית Keyera של הבעלים, המאפשרת פתיחת שער מרחוק.</li>
  <li><strong>הפעלה / השבתה</strong> — שלוט בגישת הרכב מבלי להסיר את הרשומה.</li>
  <li><strong>חיפוש וסינון</strong> — חפש לפי לוחית רישוי, שם בעלים או סטטוס.</li>
  <li><strong>ייצוא</strong> — ייצא את רשימת הרכבים לאקסל לצורכי ביקורת.</li>
</ul>
<div class="manual-note"><i class="pi pi-exclamation-circle"></i><span><strong>הערה:</strong> ניהול רכבים דורש הרשאת <em>רכבים</em>. פנה למנהל אם סעיף זה אינו גלוי.</span></div>`,
  },

  'ac-hme-body': {
    en: `<p>HME (Hardware Management Equipment) manages intercom and speaker hardware connected to your access control system. It allows sending audio announcements and managing broadcast groups to physical devices installed at gates and entry points.</p>
<ul class="manual-list">
  <li><strong>Device List</strong> — View all registered HME devices with their location and status.</li>
  <li><strong>Call Groups</strong> — Assign devices to groups for targeted audio announcements.</li>
  <li><strong>Announcements</strong> — Broadcast messages to individual devices or groups.</li>
  <li><strong>Status Monitoring</strong> — See which devices are online or offline in real time.</li>
</ul>`,
    he: `<p>HME (ציוד ניהול חומרה) מנהל אינטרקום ורמקולים המחוברים למערכת בקרת הכניסה. הוא מאפשר שליחת הודעות קוליות וניהול קבוצות שידור להתקנים פיזיים המותקנים בשערים ובנקודות כניסה.</p>
<ul class="manual-list">
  <li><strong>רשימת התקנים</strong> — צפה בכל התקני HME הרשומים עם מיקומם וסטטוסם.</li>
  <li><strong>קבוצות קריאה</strong> — שייך התקנים לקבוצות להודעות קוליות ממוקדות.</li>
  <li><strong>הודעות</strong> — שדר הודעות להתקנים בודדים או לקבוצות.</li>
  <li><strong>ניטור סטטוס</strong> — ראה אילו התקנים מחוברים ואילו לא בזמן אמת.</li>
</ul>`,
  },

  'ac-employees-body': {
    en: `<p>The Employees section manages your organization's staff — their profiles, assigned areas, work schedules, and access permissions.</p>
<h3 class="manual-subtitle">Adding an Employee</h3>
<ol class="manual-steps">
  <li>Navigate to <strong>Authorized &gt; Employees</strong>.</li>
  <li>Click <strong>Add Employee</strong>.</li>
  <li>Fill in personal details: full name, national ID, mobile, email, and job role.</li>
  <li>Assign <strong>allowed areas</strong> and set <strong>time limitations</strong> (working hours, days).</li>
  <li>Optionally set a <strong>vehicle</strong> linked to this employee.</li>
  <li>Click <strong>Save</strong> — the employee receives their digital pass in Keyera.</li>
</ol>
<ul class="manual-list">
  <li><strong>Bulk Import</strong> — Use the Excel template (download from Help Center &gt; Files) to import many employees at once.</li>
  <li><strong>Area Management</strong> — Control which specific areas each employee can access.</li>
  <li><strong>Deactivate on Leave</strong> — Temporarily deactivate an employee's pass during sick leave or vacation.</li>
</ul>`,
    he: `<p>סעיף העובדים מנהל את כוח האדם של הארגון — פרופילים, אזורים מוקצים, לוחות זמנים והרשאות גישה.</p>
<h3 class="manual-subtitle">הוספת עובד</h3>
<ol class="manual-steps">
  <li>נווט ל<strong>מורשים &gt; עובדים</strong>.</li>
  <li>לחץ <strong>הוסף עובד</strong>.</li>
  <li>מלא פרטים אישיים: שם מלא, מספר זהות, נייד, דואר אלקטרוני ותפקיד.</li>
  <li>הקצה <strong>אזורים מורשים</strong> והגדר <strong>הגבלות זמן</strong> (שעות עבודה, ימים).</li>
  <li>לחלופין, קשר <strong>רכב</strong> לעובד זה.</li>
  <li>לחץ <strong>שמור</strong> — העובד יקבל את תעודת הכניסה הדיגיטלית שלו ב-Keyera.</li>
</ol>
<ul class="manual-list">
  <li><strong>ייבוא צובר</strong> — השתמש בתבנית האקסל (הורד ממרכז עזרה &gt; קבצים) לייבוא עובדים רבים בבת אחת.</li>
  <li><strong>ניהול אזורים</strong> — שלוט לאילו אזורים כל עובד יכול לגשת.</li>
  <li><strong>השבתה בחופשה</strong> — השבת זמנית את תעודת העובד בזמן מחלה או חופשה.</li>
</ul>`,
  },

  'ac-messages-body': {
    en: `<p>The Messages section allows sending targeted communications to authorized persons — manually or via automated rules.</p>
<h3 class="manual-subtitle">Manual Messages</h3>
<ol class="manual-steps">
  <li>Navigate to <strong>Authorized &gt; Messages</strong>.</li>
  <li>Click <strong>New Message</strong>.</li>
  <li>Select recipients (all authorized, a specific group, or individuals).</li>
  <li>Choose the channel: <strong>SMS</strong>, <strong>Push Notification</strong>, or both.</li>
  <li>Compose the message and click <strong>Send</strong>.</li>
</ol>
<h3 class="manual-subtitle">Rule-Based Messages</h3>
<p>Automated messages are triggered by system events — pass expiry, entry/exit, or a schedule.</p>
<ul class="manual-list">
  <li>Navigate to the <strong>Rule-Based</strong> tab.</li>
  <li>Click <strong>Add Rule</strong> and define the trigger, delay, and message template.</li>
  <li>Rules run automatically without manual intervention.</li>
</ul>`,
    he: `<p>סעיף ההודעות מאפשר שליחת תקשורת ממוקדת לאנשים מורשים — ידנית או דרך כללים אוטומטיים.</p>
<h3 class="manual-subtitle">הודעות ידניות</h3>
<ol class="manual-steps">
  <li>נווט ל<strong>מורשים &gt; הודעות</strong>.</li>
  <li>לחץ <strong>הודעה חדשה</strong>.</li>
  <li>בחר נמענים (כל המורשים, קבוצה ספציפית או יחידים).</li>
  <li>בחר ערוץ: <strong>SMS</strong>, <strong>התראת Push</strong>, או שניהם.</li>
  <li>חבר את ההודעה ולחץ <strong>שלח</strong>.</li>
</ol>
<h3 class="manual-subtitle">הודעות מבוססות-כללים</h3>
<p>הודעות אוטומטיות מופעלות על-ידי אירועי מערכת — תפוגת תעודה, כניסה/יציאה, או לוח זמנים.</p>
<ul class="manual-list">
  <li>נווט ללשונית <strong>מבוסס-כללים</strong>.</li>
  <li>לחץ <strong>הוסף כלל</strong> והגדר את הטריגר, ההשהיה ותבנית ההודעה.</li>
  <li>הכללים פועלים אוטומטית ללא התערבות ידנית.</li>
</ul>`,
  },

  'ac-reports-body': {
    en: `<p>Reports give detailed insights into access events, attendance, and pass usage. All reports can be exported to Excel.</p>
<ul class="manual-list">
  <li><strong>Entry/Exit Log</strong> — Chronological record of all scans with person, area, timestamp, and gate.</li>
  <li><strong>Attendance Report</strong> — Shows who was present during a given period, with duration calculations.</li>
  <li><strong>Pass Status Report</strong> — Lists all passes with their current status (active, inactive, expired).</li>
  <li><strong>Area Report</strong> — Shows occupancy and traffic per access area.</li>
</ul>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>Tip:</strong> Use the date range picker to filter report data. Long date ranges may take a moment to load.</span></div>`,
    he: `<p>דוחות מספקים תובנות מפורטות על אירועי גישה, נוכחות ושימוש בתעודות. כל הדוחות ניתנים לייצוא לאקסל.</p>
<ul class="manual-list">
  <li><strong>יומן כניסות/יציאות</strong> — רשומה כרונולוגית של כל הסריקות עם שם, אזור, חותמת זמן ושער.</li>
  <li><strong>דוח נוכחות</strong> — מציג מי היה נוכח בתקופה נתונה עם חישובי משך.</li>
  <li><strong>דוח סטטוס תעודות</strong> — רשימת כל התעודות עם סטטוס נוכחי (פעיל, לא פעיל, פג).</li>
  <li><strong>דוח אזור</strong> — מציג תפוסה ותנועה לכל אזור גישה.</li>
</ul>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>טיפ:</strong> השתמש בבורר טווח תאריכים לסינון נתוני הדוח. טווחים ארוכים עלולים להימשך רגע לטעינה.</span></div>`,
  },

  'culture-body': {
    en: `<p>The Sports &amp; Culture module is designed for organizations that run public events, shows, sports competitions, and cultural activities. It manages events, ticketing, subscriber memberships, and audience communications.</p>`,
    he: `<p>מודול תרבות וספורט מיועד לארגונים המנהלים אירועים ציבוריים, הופעות, תחרויות ספורט ופעילויות תרבות. הוא מנהל אירועים, כרטוס, מנויי מנויים ותקשורת עם קהל.</p>`,
  },

  'cu-dashboard-body': {
    en: `<p>The Culture Dashboard provides a high-level view of your organization's event activity.</p>
<ul class="manual-list">
  <li><strong>KPI Row</strong> — Total events, active events, tickets issued, and people currently inside.</li>
  <li><strong>Event Timeline</strong> — Upcoming events sorted by date with quick-access links.</li>
  <li><strong>Capacity Charts</strong> — Visual gauges showing how full each event is.</li>
</ul>`,
    he: `<p>לוח המחוונים של התרבות מספק תצוגה כוללת של פעילות האירועים של הארגון.</p>
<ul class="manual-list">
  <li><strong>שורת KPI</strong> — סך אירועים, אירועים פעילים, כרטיסים שהונפקו ואנשים נוכחים כעת.</li>
  <li><strong>ציר זמן אירועים</strong> — אירועים קרובים ממוינים לפי תאריך עם קישורי גישה מהירה.</li>
  <li><strong>גרפי קיבולת</strong> — מדידים חזותיים המציגים כמה כל אירוע מלא.</li>
</ul>`,
  },

  'cu-events-body': {
    en: `<p>The Events section is the heart of the Sports &amp; Culture module. Here you create, edit, and monitor all events.</p>
<h3 class="manual-subtitle">Creating an Event</h3>
<ol class="manual-steps">
  <li>Navigate to <strong>Events</strong> in the sidebar.</li>
  <li>Click <strong>New Event</strong>.</li>
  <li>Enter event name, category, opening time, start time, and closing time.</li>
  <li>Select a <strong>Location</strong> from the predefined locations list.</li>
  <li>Set the <strong>capacity</strong> and event password if required.</li>
  <li>Optionally upload an event image and configure sub-events.</li>
  <li>Click <strong>Save</strong>. The event is now active and can receive passes.</li>
</ol>
<h3 class="manual-subtitle">Managing Events</h3>
<ul class="manual-list">
  <li><strong>Edit</strong> — Update event details at any time.</li>
  <li><strong>Cancel / Postpone</strong> — Deactivate or reschedule. Pass holders are notified automatically.</li>
  <li><strong>Monitor</strong> — Open the live entry monitor for a specific event.</li>
  <li><strong>Sub-Events</strong> — Create child events under a parent for multi-stage venues.</li>
  <li><strong>Export Passes</strong> — Download the attendee list as Excel.</li>
</ul>`,
    he: `<p>סעיף האירועים הוא לב מודול תרבות וספורט. כאן אתה יוצר, עורך ומנטר את כל האירועים.</p>
<h3 class="manual-subtitle">יצירת אירוע</h3>
<ol class="manual-steps">
  <li>נווט ל<strong>אירועים</strong> בסרגל הצד.</li>
  <li>לחץ <strong>אירוע חדש</strong>.</li>
  <li>הכנס שם אירוע, קטגוריה, שעת פתיחה, שעת התחלה ושעת סיום.</li>
  <li>בחר <strong>מיקום</strong> מרשימת המיקומים המוגדרים מראש.</li>
  <li>הגדר <strong>קיבולת</strong> וסיסמת אירוע אם נדרש.</li>
  <li>לחלופין, העלה תמונת אירוע והגדר תת-אירועים.</li>
  <li>לחץ <strong>שמור</strong>. האירוע פעיל כעת ויכול לקבל תעודות.</li>
</ol>
<h3 class="manual-subtitle">ניהול אירועים</h3>
<ul class="manual-list">
  <li><strong>עריכה</strong> — עדכן פרטי אירוע בכל עת.</li>
  <li><strong>ביטול / דחייה</strong> — השבת או קבע מחדש. מחזיקי תעודות מקבלים התראה אוטומטית.</li>
  <li><strong>ניטור</strong> — פתח את ניטור הכניסות הפעיל לאירוע ספציפי.</li>
  <li><strong>תת-אירועים</strong> — צור אירועי ילד תחת אב לאתרים מרובי שלבים.</li>
  <li><strong>ייצוא תעודות</strong> — הורד את רשימת המשתתפים לאקסל.</li>
</ul>`,
  },

  'cu-subscribers-body': {
    en: `<p>Subscribers are people with ongoing season memberships or recurring access to your facility.</p>
<ul class="manual-list">
  <li><strong>Add Subscriber</strong> — Register a person as a subscriber with a membership start and end date.</li>
  <li><strong>Membership Tiers</strong> — Assign different membership levels with different access rights.</li>
  <li><strong>Renewal</strong> — Extend a membership expiry date directly from the subscriber record.</li>
  <li><strong>Bulk Import</strong> — Import subscriber lists from Excel.</li>
  <li><strong>Subscriber Pass</strong> — Each subscriber receives a pass in Keyera that auto-validates for eligible events.</li>
</ul>`,
    he: `<p>מנויים הם אנשים עם מנויי עונה שוטפים או גישה חוזרת למתקן שלך.</p>
<ul class="manual-list">
  <li><strong>הוספת מנוי</strong> — רשום אדם כמנוי עם תאריכי התחלה וסיום של המנוי.</li>
  <li><strong>רמות מנוי</strong> — הקצה רמות מנוי שונות עם זכויות גישה שונות.</li>
  <li><strong>חידוש</strong> — הארך את תאריך התפוגה של מנוי ישירות מרשומת המנוי.</li>
  <li><strong>ייבוא צובר</strong> — ייבא רשימות מנויים מאקסל.</li>
  <li><strong>תעודת מנוי</strong> — כל מנוי מקבל תעודה ב-Keyera שמתאמתת אוטומטית לאירועים מתאימים.</li>
</ul>`,
  },

  'cu-messages-body': {
    en: `<p>Send messages to event attendees or all subscribers — useful for reminders, cancellations, or general announcements.</p>
<ul class="manual-list">
  <li>Select a specific event to message its ticket holders, or choose a subscriber group.</li>
  <li>Supports <strong>SMS</strong> and <strong>push notification</strong> channels.</li>
  <li>Schedule messages to be sent in advance (e.g., "Event starts in 1 hour").</li>
  <li>View message delivery history and status.</li>
</ul>`,
    he: `<p>שלח הודעות למשתתפי אירוע או לכל המנויים — שימושי לתזכורות, ביטולים או הודעות כלליות.</p>
<ul class="manual-list">
  <li>בחר אירוע ספציפי להודעה למחזיקי כרטיסיו, או בחר קבוצת מנויים.</li>
  <li>תמיכה בערוצי <strong>SMS</strong> ו<strong>התראת Push</strong>.</li>
  <li>תזמן הודעות לשליחה מראש (למשל, "האירוע מתחיל בעוד שעה").</li>
  <li>צפה בהיסטוריית מסירת הודעות וסטטוס.</li>
</ul>`,
  },

  'courses-body': {
    en: `<p>The Courses module is tailored for educational institutions, training centers, and organizations that run structured learning programs. It manages course catalogs, class sessions, student enrollment, and attendance reporting.</p>`,
    he: `<p>מודול הקורסים מותאם למוסדות חינוך, מרכזי הכשרה וארגונים המנהלים תוכניות לימוד מובנות. הוא מנהל קטלוגי קורסים, מפגשי שיעורים, רישום תלמידים ודיווח נוכחות.</p>`,
  },

  'co-dashboard-body': {
    en: `<p>The Courses Dashboard presents your active courses as a visual grid of tiles or a compact list.</p>
<ul class="manual-list">
  <li><strong>Grid View</strong> — Visual course tiles with key metrics and a quick-action menu.</li>
  <li><strong>List View</strong> — Tabular view with all courses, sortable by name, start date, or enrollment.</li>
  <li><strong>Upcoming Sessions Bar</strong> — A banner showing the next scheduled class sessions across all courses.</li>
</ul>`,
    he: `<p>לוח המחוונים של הקורסים מציג את הקורסים הפעילים שלך כרשת אריחים חזותית או כרשימה קומפקטית.</p>
<ul class="manual-list">
  <li><strong>תצוגת רשת</strong> — אריחי קורס חזותיים עם מדדים מרכזיים ותפריט פעולות מהירות.</li>
  <li><strong>תצוגת רשימה</strong> — תצוגה טבלאית של כל הקורסים, מסוגלת לפי שם, תאריך התחלה או רישום.</li>
  <li><strong>שורת מפגשים קרובים</strong> — באנר המציג את מפגשי השיעורים הבאים בכל הקורסים.</li>
</ul>`,
  },

  'co-events-body': {
    en: `<p>In the Courses module, "events" represent individual class sessions or lessons within a course.</p>
<ul class="manual-list">
  <li><strong>Create Session</strong> — Add a class session with date, time, room/location, and maximum participants.</li>
  <li><strong>Recurring Sessions</strong> — Schedule repeating sessions for the duration of the course.</li>
  <li><strong>Session Attendance</strong> — View who attended each session via the entry monitor or reports.</li>
  <li><strong>Cancel Session</strong> — Cancel a specific session while keeping the rest of the course intact.</li>
</ul>`,
    he: `<p>במודול הקורסים, "אירועים" מייצגים מפגשי שיעורים בודדים בקורס.</p>
<ul class="manual-list">
  <li><strong>יצירת מפגש</strong> — הוסף מפגש שיעור עם תאריך, שעה, חדר/מיקום ומספר משתתפים מרבי.</li>
  <li><strong>מפגשים חוזרים</strong> — תזמן מפגשים חוזרים לאורך הקורס.</li>
  <li><strong>נוכחות מפגש</strong> — ראה מי נכח בכל מפגש דרך ניטור הכניסות או דוחות.</li>
  <li><strong>ביטול מפגש</strong> — בטל מפגש ספציפי תוך שמירת שאר הקורס.</li>
</ul>`,
  },

  'co-students-body': {
    en: `<p>Students are enrolled participants in one or more courses. Their enrollment is managed as access passes for the course sessions.</p>
<ol class="manual-steps">
  <li>Navigate to <strong>Courses &gt; Students</strong>.</li>
  <li>Click <strong>Enroll Student</strong>.</li>
  <li>Enter student details: name, ID, mobile, and email.</li>
  <li>Select the course(s) to enroll the student in.</li>
  <li>The student receives a pass in Keyera granting access to all enrolled course sessions.</li>
</ol>
<ul class="manual-list">
  <li><strong>Bulk Enrollment</strong> — Import a student list from Excel.</li>
  <li><strong>Transfer</strong> — Move a student between courses.</li>
  <li><strong>Deactivate</strong> — Suspend a student's access without removing their record.</li>
</ul>`,
    he: `<p>תלמידים הם משתתפים רשומים בקורס אחד או יותר. הרישום שלהם מנוהל כתעודות גישה למפגשי הקורס.</p>
<ol class="manual-steps">
  <li>נווט ל<strong>קורסים &gt; תלמידים</strong>.</li>
  <li>לחץ <strong>רשום תלמיד</strong>.</li>
  <li>הכנס פרטי תלמיד: שם, זהות, נייד ודואר אלקטרוני.</li>
  <li>בחר את הקורס/ים לרישום התלמיד.</li>
  <li>התלמיד מקבל תעודה ב-Keyera המעניקה גישה לכל מפגשי הקורס הרשום.</li>
</ol>
<ul class="manual-list">
  <li><strong>רישום צובר</strong> — ייבא רשימת תלמידים מאקסל.</li>
  <li><strong>העברה</strong> — העבר תלמיד בין קורסים.</li>
  <li><strong>השבתה</strong> — השהה את גישת התלמיד מבלי להסיר את רשומתו.</li>
</ul>`,
  },

  'co-reports-body': {
    en: `<p>Course reports track student attendance, session completion rates, and overall enrollment trends.</p>
<ul class="manual-list">
  <li><strong>Attendance per Session</strong> — Who attended each class, with timestamps.</li>
  <li><strong>Student Attendance Summary</strong> — Overall attendance percentage per student across all sessions.</li>
  <li><strong>Course Completion</strong> — Which students attended all required sessions.</li>
  <li>All reports export to Excel with a single click.</li>
</ul>`,
    he: `<p>דוחות קורסים עוקבים אחר נוכחות תלמידים, שיעורי השלמת מפגשים ומגמות רישום כוללות.</p>
<ul class="manual-list">
  <li><strong>נוכחות לפי מפגש</strong> — מי נכח בכל שיעור עם חותמות זמן.</li>
  <li><strong>סיכום נוכחות תלמיד</strong> — אחוז נוכחות כולל לכל תלמיד בכל המפגשים.</li>
  <li><strong>השלמת קורס</strong> — אילו תלמידים נכחו בכל המפגשים הנדרשים.</li>
  <li>כל הדוחות מיוצאים לאקסל בלחיצה אחת.</li>
</ul>`,
  },

  'residential-body': {
    en: `<p>The Residential module is designed for gated communities, apartment complexes, and residential buildings. It manages resident access passes, visitor invitations, apartment directories, and resident communications.</p>`,
    he: `<p>מודול המגורים מיועד לקהילות מגודרות, מתחמי דירות ובניינים למגורים. הוא מנהל תעודות כניסה לדיירים, הזמנות מבקרים, ספריות דירות ותקשורת עם דיירים.</p>`,
  },

  're-dashboard-body': {
    en: `<p>The Residential Dashboard provides a real-time view of who is currently inside the complex and overall access activity.</p>
<ul class="manual-list">
  <li><strong>Occupancy Summary</strong> — How many residents and visitors are currently inside.</li>
  <li><strong>Entry Graph</strong> — Hourly entry/exit trends for the current day.</li>
  <li><strong>Alert Panel</strong> — Unauthorized access attempts or system alerts.</li>
</ul>`,
    he: `<p>לוח המחוונים של המגורים מספק תצוגה בזמן אמת של מי נמצא כעת במתחם ופעילות הגישה הכוללת.</p>
<ul class="manual-list">
  <li><strong>סיכום תפוסה</strong> — כמה דיירים ומבקרים נמצאים כעת בפנים.</li>
  <li><strong>גרף כניסות</strong> — מגמות כניסה/יציאה שעתיות ליום הנוכחי.</li>
  <li><strong>לוח התראות</strong> — ניסיונות גישה לא מורשים או התראות מערכת.</li>
</ul>`,
  },

  're-passes-body': {
    en: `<p>Resident passes grant permanent or long-term access to the complex. Visitor passes can be issued by residents for guests.</p>
<ul class="manual-list">
  <li><strong>Resident Pass</strong> — Issued to apartment residents for indefinite access, linked to a specific apartment unit.</li>
  <li><strong>Visitor Invitation</strong> — A resident can issue a temporary visitor pass with specific date/time constraints.</li>
  <li><strong>Guest Management</strong> — View all active guest passes and revoke them if needed.</li>
  <li><strong>Area-Based Access</strong> — Different passes for lobby, parking, gym, pool — each with its own rules.</li>
</ul>`,
    he: `<p>תעודות דיירים מעניקות גישה קבועה או לטווח ארוך למתחם. תעודות מבקרים יכולות להיות מונפקות על-ידי דיירים לאורחים.</p>
<ul class="manual-list">
  <li><strong>תעודת דייר</strong> — מונפקת לדיירי הדירה לגישה ללא הגבלת זמן, מקושרת ליחידת דירה ספציפית.</li>
  <li><strong>הזמנת מבקר</strong> — דייר יכול להנפיק תעודת מבקר זמנית עם אילוצי תאריך/שעה ספציפיים.</li>
  <li><strong>ניהול אורחים</strong> — צפה בכל תעודות האורחים הפעילות ובטל אותן במידת הצורך.</li>
  <li><strong>גישה מבוססת-אזור</strong> — תעודות שונות ללובי, חניון, חדר כושר, בריכה — כל אחת עם כלליה שלה.</li>
</ul>`,
  },

  're-apartments-body': {
    en: `<p>The Apartments section maintains the directory of all units in the residential complex.</p>
<ul class="manual-list">
  <li><strong>Add Apartment</strong> — Create an apartment record with building, floor, and unit number.</li>
  <li><strong>Assign Residents</strong> — Link residents to an apartment and issue their access passes.</li>
  <li><strong>View Apartment History</strong> — Track which residents lived in an apartment and when.</li>
  <li><strong>Vacancy Status</strong> — Mark apartments as occupied or vacant.</li>
</ul>`,
    he: `<p>סעיף הדירות מתחזק את ספריית כל היחידות במתחם המגורים.</p>
<ul class="manual-list">
  <li><strong>הוספת דירה</strong> — צור רשומת דירה עם בניין, קומה ומספר יחידה.</li>
  <li><strong>הקצאת דיירים</strong> — קשר דיירים לדירה והנפק את תעודות הכניסה שלהם.</li>
  <li><strong>צפייה בהיסטוריית דירה</strong> — עקוב אחר אילו דיירים התגוררו בדירה ומתי.</li>
  <li><strong>סטטוס פנוי/תפוס</strong> — סמן דירות כתפוסות או פנויות.</li>
</ul>`,
  },

  're-messages-body': {
    en: `<p>Send targeted announcements to residents of the entire complex, specific buildings, or individual apartments.</p>
<ul class="manual-list">
  <li>Broadcast maintenance notices, community announcements, or security alerts.</li>
  <li>Filter recipients by building, floor, or apartment.</li>
  <li>Supports SMS and push notification delivery channels.</li>
  <li>Rule-based messages for automated reminders (e.g., lease renewal alerts).</li>
</ul>`,
    he: `<p>שלח הודעות ממוקדות לדיירי המתחם כולו, בנייני ספציפיים או דירות בודדות.</p>
<ul class="manual-list">
  <li>שדר הודעות תחזוקה, הודעות קהילה או התראות אבטחה.</li>
  <li>סנן נמענים לפי בניין, קומה או דירה.</li>
  <li>תמיכה בערוצי SMS והתראת Push.</li>
  <li>הודעות מבוססות-כללים לתזכורות אוטומטיות (למשל, התראות חידוש חוזה).</li>
</ul>`,
  },

  'kindergarten-body': {
    en: `<p>The Kindergarten module provides a child safety and access management solution for preschools and daycare centers. It tracks which children are present, manages authorized pick-up persons, and facilitates parent communication.</p>`,
    he: `<p>מודול הגן מספק פתרון בטיחות ילדים וניהול גישה לגני ילדים ומעונות. הוא עוקב אחר ילדים הנוכחים, מנהל אנשים מורשים לאיסוף ומקל על תקשורת עם הורים.</p>`,
  },

  'ki-dashboard-body': {
    en: `<p>The Kindergarten Dashboard shows a real-time view of children currently present, those who have not arrived, and time overruns.</p>
<ul class="manual-list">
  <li><strong>Present / Absent Count</strong> — At-a-glance numbers of children inside and those expected but not yet arrived.</li>
  <li><strong>Overtime Alert</strong> — Highlights children who have been in the facility longer than their scheduled hours.</li>
  <li><strong>Staff Overview</strong> — Shows which teachers/employees are currently on duty.</li>
  <li><strong>Area View</strong> — If the kindergarten has multiple rooms/groups, see the occupancy per room.</li>
</ul>`,
    he: `<p>לוח המחוונים של הגן מציג תצוגה בזמן אמת של ילדים הנוכחים כעת, אלו שלא הגיעו וחריגות זמן.</p>
<ul class="manual-list">
  <li><strong>ספירת נוכחים/נעדרים</strong> — מספרים ממבט ראשון של ילדים בפנים ואלה שמצפה להם אך טרם הגיעו.</li>
  <li><strong>התראת שעות נוספות</strong> — מדגיש ילדים שהיו במתקן זמן ארוך יותר מהשעות המתוכננות.</li>
  <li><strong>סקירת צוות</strong> — מציג אילו מורים/עובדים בתפקיד כעת.</li>
  <li><strong>תצוגת אזור</strong> — אם לגן יש מספר חדרים/קבוצות, ראה את התפוסה לכל חדר.</li>
</ul>`,
  },

  'ki-passes-body': {
    en: `<p>Authorized passes in the kindergarten context are issued to parents, guardians, and authorized pick-up persons — not to the children themselves.</p>
<ul class="manual-list">
  <li><strong>Issue Pick-Up Pass</strong> — Create a pass for a parent or guardian with their photo and ID.</li>
  <li><strong>Linked Children</strong> — A pass is linked to one or more children that the holder is authorized to pick up.</li>
  <li><strong>Time Restrictions</strong> — Set permitted pick-up hours (e.g., only after 12:00).</li>
  <li><strong>One-Time Guest</strong> — Issue a temporary single-use pass for a substitute pick-up person.</li>
  <li><strong>Revoke</strong> — Immediately deactivate a pass if a custody situation changes.</li>
</ul>
<div class="manual-note"><i class="pi pi-exclamation-circle"></i><span><strong>Note:</strong> The gate scanner verifies the pass AND checks the linked children list before allowing entry.</span></div>`,
    he: `<p>תעודות מורשות בהקשר הגן מונפקות להורים, אפוטרופוסים ואנשים מורשים לאיסוף — לא לילדים עצמם.</p>
<ul class="manual-list">
  <li><strong>הנפקת תעודת איסוף</strong> — צור תעודה להורה או אפוטרופוס עם תמונתם ומזהה.</li>
  <li><strong>ילדים מקושרים</strong> — תעודה מקושרת לילד אחד או יותר שהמחזיק מורשה לאסוף.</li>
  <li><strong>הגבלות שעה</strong> — הגדר שעות איסוף מורשות (למשל, רק אחרי 12:00).</li>
  <li><strong>אורח חד-פעמי</strong> — הנפק תעודה זמנית לשימוש חד-פעמי לאדם איסוף חלופי.</li>
  <li><strong>ביטול</strong> — השבת מיידית תעודה אם מצב משמורת משתנה.</li>
</ul>
<div class="manual-note"><i class="pi pi-exclamation-circle"></i><span><strong>הערה:</strong> סורק השער מאמת את התעודה ובודק את רשימת הילדים המקושרים לפני מתן כניסה.</span></div>`,
  },

  'ki-children-body': {
    en: `<p>The Children section is the central registry of all enrolled children.</p>
<ol class="manual-steps">
  <li>Navigate to <strong>Kindergarten &gt; Children</strong>.</li>
  <li>Click <strong>Add Child</strong>.</li>
  <li>Enter the child's name, date of birth, class/group, and any medical notes.</li>
  <li>Link authorized guardians (parents) who are allowed to pick up this child.</li>
  <li>Set the child's standard arrival and pick-up times.</li>
  <li>Click <strong>Save</strong>.</li>
</ol>
<ul class="manual-list">
  <li><strong>Attendance History</strong> — See the child's daily check-in/check-out log.</li>
  <li><strong>Class Assignment</strong> — Move a child between classes without losing their history.</li>
  <li><strong>Medical Notes</strong> — Store allergy and medical information accessible to staff.</li>
</ul>`,
    he: `<p>סעיף הילדים הוא המרשם המרכזי של כל הילדים הרשומים.</p>
<ol class="manual-steps">
  <li>נווט ל<strong>גן ילדים &gt; ילדים</strong>.</li>
  <li>לחץ <strong>הוסף ילד</strong>.</li>
  <li>הכנס שם הילד, תאריך לידה, כיתה/קבוצה וכל הערות רפואיות.</li>
  <li>קשר אפוטרופוסים מורשים (הורים) המותרים לאסוף ילד זה.</li>
  <li>הגדר שעות הגעה ואיסוף רגילות של הילד.</li>
  <li>לחץ <strong>שמור</strong>.</li>
</ol>
<ul class="manual-list">
  <li><strong>היסטוריית נוכחות</strong> — ראה את יומן הצ'ק-אין/צ'ק-אאוט היומי של הילד.</li>
  <li><strong>שיוך כיתה</strong> — העבר ילד בין כיתות מבלי לאבד את ההיסטוריה שלו.</li>
  <li><strong>הערות רפואיות</strong> — שמור מידע על אלרגיות ומצב רפואי הנגיש לצוות.</li>
</ul>`,
  },

  'ki-employees-body': {
    en: `<p>Teaching staff and aides are managed in the Employees section. They receive employee-type access passes for the facility.</p>
<ul class="manual-list">
  <li>Add teachers and assistants with their personal details and class assignments.</li>
  <li>Set working hours — the system tracks their presence against scheduled hours.</li>
  <li>Generate overtime alerts when an employee stays beyond their scheduled shift.</li>
  <li>Bulk-import staff records from Excel.</li>
</ul>`,
    he: `<p>צוות ההוראה והסייעות מנוהלים בסעיף העובדים. הם מקבלים תעודות כניסה מסוג עובד למתקן.</p>
<ul class="manual-list">
  <li>הוסף מורים ועוזרים עם פרטיהם האישיים ושיוכי הכיתה.</li>
  <li>הגדר שעות עבודה — המערכת עוקבת אחר נוכחותם מול השעות המתוכננות.</li>
  <li>צור התראות שעות נוספות כאשר עובד נשאר מעבר למשמרת המתוכננת.</li>
  <li>ייבא רשומות צוות בצובר מאקסל.</li>
</ul>`,
  },

  'ki-messages-body': {
    en: `<p>Communicate with parents and guardians through the Kindergarten Messages section.</p>
<ul class="manual-list">
  <li><strong>Class Broadcast</strong> — Send a message to all parents of children in a specific class.</li>
  <li><strong>Individual Message</strong> — Send directly to a specific parent's mobile number.</li>
  <li><strong>Automated Alerts</strong> — Rule-based messages such as "Your child has arrived" or "Pick-up time in 15 minutes".</li>
  <li><strong>Channel</strong> — SMS or push notification via the Keyera app.</li>
</ul>`,
    he: `<p>תקשר עם הורים ואפוטרופוסים דרך סעיף הודעות הגן.</p>
<ul class="manual-list">
  <li><strong>שידור כיתה</strong> — שלח הודעה לכל ההורים של ילדים בכיתה ספציפית.</li>
  <li><strong>הודעה אישית</strong> — שלח ישירות למספר הנייד של הורה ספציפי.</li>
  <li><strong>התראות אוטומטיות</strong> — הודעות מבוססות-כללים כגון "ילדך הגיע" או "זמן איסוף בעוד 15 דקות".</li>
  <li><strong>ערוץ</strong> — SMS או התראת Push דרך אפליקציית Keyera.</li>
</ul>`,
  },

  'ki-reports-body': {
    en: `<p>Generate detailed reports on child attendance, staff presence, and pick-up activity.</p>
<ul class="manual-list">
  <li><strong>Daily Attendance</strong> — Who was present each day, arrival and departure times.</li>
  <li><strong>Overtime Report</strong> — Children who stayed beyond their scheduled pick-up time.</li>
  <li><strong>Guardian Activity</strong> — Log of all pick-up events by guardian.</li>
  <li><strong>Staff Report</strong> — Teacher attendance and hours worked.</li>
</ul>`,
    he: `<p>צור דוחות מפורטים על נוכחות ילדים, נוכחות צוות ופעילות איסוף.</p>
<ul class="manual-list">
  <li><strong>נוכחות יומית</strong> — מי נכח כל יום, שעות הגעה ועזיבה.</li>
  <li><strong>דוח שעות נוספות</strong> — ילדים שנשארו מעבר לשעת האיסוף המתוכננת.</li>
  <li><strong>פעילות אפוטרופוס</strong> — יומן כל אירועי האיסוף לפי אפוטרופוס.</li>
  <li><strong>דוח צוות</strong> — נוכחות מורים ושעות עבודה.</li>
</ul>`,
  },

  'nursing-body': {
    en: `<p>The Nursing module serves care facilities, nursing homes, and assisted living centers. It manages resident passes (patients), staff access, care orders, and family visitor management.</p>`,
    he: `<p>מודול הסיעוד משרת מתקני טיפול, בתי אבות ומרכזי דיור מוגן. הוא מנהל תעודות דיירים (מטופלים), גישת צוות, הזמנות טיפול וניהול מבקרי משפחה.</p>`,
  },

  'nu-dashboard-body': {
    en: `<p>The Nursing Dashboard provides an overview of patients, visitors, and staff currently in the facility.</p>
<ul class="manual-list">
  <li><strong>Occupancy by Ward</strong> — How many patients and visitors are in each area.</li>
  <li><strong>Staff On Duty</strong> — List of active nurses, caregivers, and administrators currently present.</li>
  <li><strong>Visitor Overview</strong> — Active visitor passes and remaining visit time.</li>
  <li><strong>Alert Panel</strong> — Notifications for unauthorized access or restricted-area breaches.</li>
</ul>`,
    he: `<p>לוח המחוונים של הסיעוד מספק סקירה של מטופלים, מבקרים וצוות הנמצאים כעת במתקן.</p>
<ul class="manual-list">
  <li><strong>תפוסה לפי מחלקה</strong> — כמה מטופלים ומבקרים נמצאים בכל אזור.</li>
  <li><strong>צוות בתפקיד</strong> — רשימת אחיות, מטפלים ומנהלים פעילים הנוכחים כעת.</li>
  <li><strong>סקירת מבקרים</strong> — תעודות מבקרים פעילות וזמן ביקור נותר.</li>
  <li><strong>לוח התראות</strong> — התראות לגישה לא מורשית או פריצות לאזורים מוגבלים.</li>
</ul>`,
  },

  'nu-orders-body': {
    en: `<p>Care Orders are service requests for specific care actions — medication changes, room transfers, or special dietary requirements.</p>
<ol class="manual-steps">
  <li>Navigate to <strong>Nursing &gt; Orders</strong>.</li>
  <li>Click <strong>New Order</strong>.</li>
  <li>Select the patient, care type, and assign to a responsible caregiver.</li>
  <li>Set priority (routine, urgent, emergency) and due date/time.</li>
  <li>Click <strong>Submit</strong>. The assigned caregiver is notified.</li>
</ol>
<ul class="manual-list">
  <li><strong>Order Tracking</strong> — View the status of all open orders (pending, in progress, completed).</li>
  <li><strong>History</strong> — Full audit trail of all care orders per patient.</li>
  <li><strong>Approval Workflow</strong> — Orders requiring doctor approval go through a review step before execution.</li>
</ul>`,
    he: `<p>הזמנות טיפול הן בקשות שירות לפעולות טיפול ספציפיות — שינויי תרופות, העברות חדר או דרישות תזונה מיוחדות.</p>
<ol class="manual-steps">
  <li>נווט ל<strong>סיעוד &gt; הזמנות</strong>.</li>
  <li>לחץ <strong>הזמנה חדשה</strong>.</li>
  <li>בחר את המטופל, סוג הטיפול והקצה למטפל אחראי.</li>
  <li>הגדר עדיפות (שגרה, דחוף, חירום) ותאריך/שעת יעד.</li>
  <li>לחץ <strong>שלח</strong>. המטפל המוקצה מקבל הודעה.</li>
</ol>
<ul class="manual-list">
  <li><strong>מעקב הזמנות</strong> — צפה בסטטוס כל ההזמנות הפתוחות (ממתין, בתהליך, הושלם).</li>
  <li><strong>היסטוריה</strong> — מסלול ביקורת מלא של כל הזמנות הטיפול לכל מטופל.</li>
  <li><strong>זרימת אישור</strong> — הזמנות הדורשות אישור רופא עוברות שלב בדיקה לפני ביצוע.</li>
</ul>`,
  },

  'nu-messages-body': {
    en: `<p>Send communications to family members, visitors, or staff. Typical use cases include visitor hour reminders, care updates, and facility announcements.</p>
<ul class="manual-list">
  <li>Send per-patient updates to registered family members.</li>
  <li>Broadcast facility-wide announcements to all family contacts.</li>
  <li>Automated rule-based messages (e.g., "Visiting hours end in 30 minutes").</li>
</ul>`,
    he: `<p>שלח תקשורת לבני משפחה, מבקרים או צוות. שימושים טיפוסיים כוללים תזכורות שעות ביקור, עדכוני טיפול והודעות מתקן.</p>
<ul class="manual-list">
  <li>שלח עדכונים לפי מטופל לבני משפחה רשומים.</li>
  <li>שדר הודעות כלל-מתקן לכל אנשי הקשר המשפחתיים.</li>
  <li>הודעות אוטומטיות מבוססות-כללים (למשל, "שעות הביקור מסתיימות בעוד 30 דקות").</li>
</ul>`,
  },

  'nu-reports-body': {
    en: `<p>Nursing reports cover patient visits, staff attendance, care order completion, and access events by ward.</p>
<ul class="manual-list">
  <li><strong>Visitor Log</strong> — All visits to the facility with duration and patient visited.</li>
  <li><strong>Care Order Report</strong> — Completion rates, response times, and pending items by caregiver.</li>
  <li><strong>Staff Attendance</strong> — Shift hours and attendance by nursing staff member.</li>
</ul>`,
    he: `<p>דוחות הסיעוד מכסים ביקורי מטופלים, נוכחות צוות, השלמת הזמנות טיפול ואירועי גישה לפי מחלקה.</p>
<ul class="manual-list">
  <li><strong>יומן מבקרים</strong> — כל הביקורים במתקן עם משך ומטופל שבוקר.</li>
  <li><strong>דוח הזמנות טיפול</strong> — שיעורי השלמה, זמני תגובה ופריטים ממתינים לפי מטפל.</li>
  <li><strong>נוכחות צוות</strong> — שעות משמרת ונוכחות לפי עובד סיעוד.</li>
</ul>`,
  },

  'timewatch-body': {
    en: `<p>Time Watch is an employee time-and-attendance management module. It tracks employee clock-in and clock-out times using Keyera passes scanned at gate terminals, enabling automated attendance and payroll-ready reports.</p>`,
    he: `<p>שעון נוכחות הוא מודול ניהול זמן ונוכחות עובדים. הוא עוקב אחר שעות כניסה ויציאה של עובדים באמצעות תעודות Keyera הנסרקות בטרמינלי שערים, מה שמאפשר דוחות נוכחות אוטומטיים ומוכנים לשכר.</p>`,
  },

  'tw-dashboard-body': {
    en: `<p>The Time Watch Dashboard shows a real-time snapshot of employee attendance for the current workday.</p>
<ul class="manual-list">
  <li><strong>Currently Present</strong> — Employees who have clocked in and are still on site.</li>
  <li><strong>Late Arrivals</strong> — Employees who arrived after their scheduled start time.</li>
  <li><strong>Early Departures</strong> — Employees who left before their scheduled end time.</li>
  <li><strong>Absentees</strong> — Employees expected but not yet clocked in.</li>
  <li><strong>Overtime</strong> — Employees still present after their scheduled end time.</li>
</ul>`,
    he: `<p>לוח המחוונים של שעון הנוכחות מציג תמונת מצב בזמן אמת של נוכחות עובדים ליום העבודה הנוכחי.</p>
<ul class="manual-list">
  <li><strong>נוכחים כעת</strong> — עובדים שצ'קו-אין ועדיין באתר.</li>
  <li><strong>איחורים</strong> — עובדים שהגיעו אחרי שעת ההתחלה המתוכננת.</li>
  <li><strong>יציאות מוקדמות</strong> — עובדים שיצאו לפני שעת הסיום המתוכננת.</li>
  <li><strong>נעדרים</strong> — עובדים שמצפה להם אך טרם צ'קו-אין.</li>
  <li><strong>שעות נוספות</strong> — עובדים עדיין נוכחים אחרי שעת הסיום המתוכננת.</li>
</ul>`,
  },

  'tw-employees-body': {
    en: `<p>Manage employee schedules and profiles used to calculate attendance accuracy.</p>
<ul class="manual-list">
  <li><strong>Work Schedule</strong> — Define daily start/end times and working days per employee or per group.</li>
  <li><strong>Break Deductions</strong> — Configure automatic deductions for lunch and break periods.</li>
  <li><strong>Manual Clock Correction</strong> — Supervisors can manually correct missed clock-ins or clock-outs.</li>
  <li><strong>Absence Codes</strong> — Mark absences with reason codes (sick leave, vacation, authorized absence).</li>
</ul>`,
    he: `<p>נהל לוחות זמנים ופרופילי עובדים המשמשים לחישוב דיוק הנוכחות.</p>
<ul class="manual-list">
  <li><strong>לוח עבודה</strong> — הגדר שעות התחלה/סיום יומיות וימי עבודה לעובד או לקבוצה.</li>
  <li><strong>ניכויי הפסקה</strong> — הגדר ניכויים אוטומטיים לארוחות צהריים והפסקות.</li>
  <li><strong>תיקון שעון ידני</strong> — מנהלים יכולים לתקן ידנית כניסות/יציאות שהוחמצו.</li>
  <li><strong>קודי היעדרות</strong> — סמן היעדרויות עם קודי סיבה (מחלה, חופשה, היעדרות מורשית).</li>
</ul>`,
  },

  'tw-messages-body': {
    en: `<p>Send attendance-related notifications to employees using rule-based messages.</p>
<ul class="manual-list">
  <li>Automated "You forgot to clock out" alert if no exit scan is recorded by end of shift.</li>
  <li>Weekly attendance summary sent to each employee.</li>
  <li>Manager notifications for employees exceeding overtime thresholds.</li>
</ul>`,
    he: `<p>שלח התראות הקשורות לנוכחות לעובדים באמצעות הודעות מבוססות-כללים.</p>
<ul class="manual-list">
  <li>התראה אוטומטית "שכחת לצ'ק-אאוט" אם לא נרשמת סריקת יציאה עד סוף המשמרת.</li>
  <li>סיכום נוכחות שבועי הנשלח לכל עובד.</li>
  <li>התראות מנהל לעובדים החורגים מסף שעות נוספות.</li>
</ul>`,
  },

  'tw-reports-body': {
    en: `<p>Time Watch reports are designed for payroll processing and HR compliance.</p>
<ul class="manual-list">
  <li><strong>Daily Attendance Report</strong> — Clock-in/out times, total hours, and status per employee per day.</li>
  <li><strong>Monthly Summary</strong> — Aggregated hours worked, overtime, and absences per employee for the month.</li>
  <li><strong>Overtime Report</strong> — Employees who exceeded standard working hours.</li>
  <li><strong>Absence Report</strong> — All absences with reason codes for the selected period.</li>
  <li><strong>Payroll Export</strong> — A formatted Excel export compatible with common payroll systems.</li>
</ul>`,
    he: `<p>דוחות שעון הנוכחות מיועדים לעיבוד שכר ותאימות HR.</p>
<ul class="manual-list">
  <li><strong>דוח נוכחות יומי</strong> — שעות כניסה/יציאה, סה"כ שעות וסטטוס לכל עובד לכל יום.</li>
  <li><strong>סיכום חודשי</strong> — שעות עבודה מצטברות, שעות נוספות והיעדרויות לכל עובד לחודש.</li>
  <li><strong>דוח שעות נוספות</strong> — עובדים שחרגו משעות עבודה רגילות.</li>
  <li><strong>דוח היעדרויות</strong> — כל ההיעדרויות עם קודי סיבה לתקופה הנבחרת.</li>
  <li><strong>ייצוא שכר</strong> — ייצוא אקסל מעוצב תואם למערכות שכר נפוצות.</li>
</ul>`,
  },

  'entry-monitor-body': {
    en: `<p>The Entry Monitor is a real-time display screen designed for security booths, reception desks, or guard stations. It shows a live feed of every entry and exit scan as it happens.</p>
<ul class="manual-list">
  <li><strong>Live Feed</strong> — Each scan appears instantly: green for valid, red for invalid.</li>
  <li><strong>Person Details</strong> — Displays the name, photo (if available), and allowed areas for the scanned pass.</li>
  <li><strong>Audio Alerts</strong> — Configurable audio cues for granted/denied entry.</li>
  <li><strong>Area Filter</strong> — Show only scans for a specific gate or area.</li>
  <li><strong>Full-Screen Mode</strong> — Designed to run on a dedicated monitor in kiosk mode.</li>
  <li><strong>No Mouse Needed</strong> — The screen is read-only and auto-refreshes.</li>
  <li><strong>Open Stations</strong> — A role with the "Open stations" permission also sees every station of the event next to the list and opens one with a single click, without being able to edit the event. Star the stations you open most to keep them in the Favorites tab.</li>
</ul>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>Tip:</strong> Access the Entry Monitor at <code>/entry-monitor</code>. For best results, open it in fullscreen on a dedicated display at your gate or reception area.</span></div>`,
    he: `<p>בקרת הכניסות היא מסך תצוגה בזמן אמת המיועד לתאי אבטחה, דלפקי קבלה או עמדות שמירה. הוא מציג עדכון חי של כל סריקת כניסה ויציאה כפי שהיא מתרחשת.</p>
<ul class="manual-list">
  <li><strong>עדכון חי</strong> — כל סריקה מופיעה מיידית: ירוק לתקף, אדום לבלתי תקף.</li>
  <li><strong>פרטי אדם</strong> — מציג שם, תמונה (אם קיימת) ואזורים מורשים לתעודה הנסרקת.</li>
  <li><strong>התראות קוליות</strong> — צלילי אות ניתנים להגדרה לכניסה מורשית/מסורבת.</li>
  <li><strong>סינון אזור</strong> — הצג רק סריקות לשער או אזור ספציפי.</li>
  <li><strong>מצב מסך מלא</strong> — מיועד לפעול על מסך ייעודי במצב קיוסק.</li>
  <li><strong>ללא עכבר</strong> — המסך לקריאה בלבד ומתרענן אוטומטית.</li>
  <li><strong>פתיחת עמדות</strong> — תפקיד עם ההרשאה "פתיחת עמדות" רואה גם את כל עמדות האירוע ליד הרשימה ופותח עמדה בלחיצה אחת, בלי הרשאה לערוך את האירוע. סמנו ★ ליד העמדות שאתם פותחים הכי הרבה כדי שיופיעו בלשונית המועדפים.</li>
</ul>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>טיפ:</strong> גשו לבקרת הכניסות בכתובת <code>/entry-monitor</code>. לתוצאות הטובות ביותר, פתח אותו במסך מלא על תצוגה ייעודית בשער או באזור הקבלה.</span></div>`,
  },

  'settings-body': {
    en: `<p>The Settings section is the administrative control center of C-Portal. Access is restricted to users with the appropriate permission level.</p>
<div class="manual-note"><i class="pi pi-exclamation-circle"></i><span><strong>Note:</strong> Changes in Settings affect the entire organization. Only system administrators should have access to this section.</span></div>`,
    he: `<p>סעיף ההגדרות הוא מרכז הבקרה המנהלתי של C-Portal. הגישה מוגבלת למשתמשים עם רמת הרשאה מתאימה.</p>
<div class="manual-note"><i class="pi pi-exclamation-circle"></i><span><strong>הערה:</strong> שינויים בהגדרות משפיעים על הארגון כולו. רק מנהלי מערכת צריכים לקבל גישה לסעיף זה.</span></div>`,
  },

  'se-business-body': {
    en: `<p>Business Details stores your organization's identity information and API credentials.</p>
<ul class="manual-list">
  <li><strong>Organization Name</strong> — The name displayed across the portal and in Keyera passes.</li>
  <li><strong>Logo</strong> — Upload your organization's logo for branding.</li>
  <li><strong>API Token</strong> — Your secret token for authenticating calls to the Yabi External API. Keep this confidential.</li>
  <li><strong>Client ID</strong> — Your numeric organization identifier, required for API calls.</li>
  <li><strong>Default Event Password</strong> — The default password used when creating events without a specific password.</li>
  <li><strong>Contact Info</strong> — Organization address, phone, and website.</li>
</ul>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>Tip:</strong> Find your API Token and Client ID here to use with the External API. See the <strong>API Reference</strong> tab for integration instructions.</span></div>`,
    he: `<p>פרטי עסק שומרים את פרטי הזהות של הארגון ואת אישורי ה-API.</p>
<ul class="manual-list">
  <li><strong>שם ארגון</strong> — השם המוצג בכל הפורטל ובתעודות Keyera.</li>
  <li><strong>לוגו</strong> — העלה את הלוגו של הארגון למיתוג.</li>
  <li><strong>אסימון API</strong> — האסימון הסודי שלך לאימות קריאות ל-Yabi External API. שמור בסוד.</li>
  <li><strong>Client ID</strong> — מזהה הארגון המספרי שלך, נדרש לקריאות API.</li>
  <li><strong>סיסמת אירוע ברירת מחדל</strong> — הסיסמה המשמשת ליצירת אירועים ללא סיסמה ספציפית.</li>
  <li><strong>פרטי קשר</strong> — כתובת הארגון, טלפון ואתר אינטרנט.</li>
</ul>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>טיפ:</strong> מצא כאן את אסימון ה-API ואת ה-Client ID לשימוש עם ה-API החיצוני. ראה את לשונית <strong>API Reference</strong> להוראות שילוב.</span></div>`,
  },

  'se-users-body': {
    en: `<p>Manage portal user accounts — the people who log in to C-Portal to manage your organization.</p>
<ul class="manual-list">
  <li><strong>Add User</strong> — Create a new portal user with email, name, and an assigned role.</li>
  <li><strong>Assign Role</strong> — Choose from predefined roles (Admin, Manager, Operator) or custom roles.</li>
  <li><strong>Deactivate</strong> — Temporarily block a user's login without deleting their account.</li>
  <li><strong>Password Reset</strong> — Send a password reset link to a user's email.</li>
  <li><strong>Audit Log</strong> — View recent login activity for each user.</li>
</ul>`,
    he: `<p>נהל חשבונות משתמשי פורטל — האנשים המתחברים ל-C-Portal לניהול הארגון שלך.</p>
<ul class="manual-list">
  <li><strong>הוספת משתמש</strong> — צור משתמש פורטל חדש עם דואר אלקטרוני, שם ותפקיד מוקצה.</li>
  <li><strong>הקצאת תפקיד</strong> — בחר מתפקידים מוגדרים מראש (מנהל, מנג'ר, מפעיל) או תפקידים מותאמים אישית.</li>
  <li><strong>השבתה</strong> — חסום זמנית כניסת משתמש מבלי למחוק את חשבונו.</li>
  <li><strong>איפוס סיסמה</strong> — שלח קישור איפוס סיסמה לדואר האלקטרוני של משתמש.</li>
  <li><strong>יומן ביקורת</strong> — צפה בפעילות כניסה אחרונה לכל משתמש.</li>
</ul>`,
  },

  'se-billing-body': {
    en: `<p>View your organization's current subscription plan, active licenses, and billing information.</p>
<ul class="manual-list">
  <li><strong>Active Plan</strong> — Current subscription tier and included features.</li>
  <li><strong>License Counts</strong> — Number of active passes, users, and devices against your plan limits.</li>
  <li><strong>Renewal Date</strong> — When the current subscription period ends.</li>
  <li><strong>Invoices</strong> — Download past invoices for accounting.</li>
</ul>
<div class="manual-note"><i class="pi pi-exclamation-circle"></i><span><strong>Note:</strong> To upgrade your plan or request additional licenses, contact your Yabi account manager.</span></div>`,
    he: `<p>צפה בתוכנית המנוי הנוכחית של הארגון, רישיונות פעילים ומידע לחיוב.</p>
<ul class="manual-list">
  <li><strong>תוכנית פעילה</strong> — רמת מנוי נוכחית ותכונות כלולות.</li>
  <li><strong>ספירות רישיונות</strong> — מספר תעודות פעילות, משתמשים והתקנים מול מגבלות התוכנית.</li>
  <li><strong>תאריך חידוש</strong> — מתי מסתיימת תקופת המנוי הנוכחית.</li>
  <li><strong>חשבוניות</strong> — הורד חשבוניות עבר לחשבונאות.</li>
</ul>
<div class="manual-note"><i class="pi pi-exclamation-circle"></i><span><strong>הערה:</strong> לשדרוג תוכניתך או בקשת רישיונות נוספים, פנה למנהל חשבון Yabi שלך.</span></div>`,
  },

  'se-locations-body': {
    en: `<p>Locations are named places used when creating events. Predefined locations save time and ensure consistency.</p>
<ol class="manual-steps">
  <li>Navigate to <strong>Settings &gt; Locations</strong>.</li>
  <li>Click <strong>Add Location</strong>.</li>
  <li>Enter the location name, address, and optionally pin it on the map.</li>
  <li>Click <strong>Save</strong>. The location is now available in the event creation form.</li>
</ol>
<ul class="manual-list">
  <li><strong>Edit / Delete</strong> — Update or remove existing locations.</li>
  <li><strong>Map Pin</strong> — Coordinates are used in the Keyera app navigation button.</li>
</ul>`,
    he: `<p>מיקומים הם מקומות בעלי שם המשמשים ביצירת אירועים. מיקומים מוגדרים מראש חוסכים זמן ומבטיחים עקביות.</p>
<ol class="manual-steps">
  <li>נווט ל<strong>הגדרות &gt; מיקומים</strong>.</li>
  <li>לחץ <strong>הוסף מיקום</strong>.</li>
  <li>הכנס שם מיקום, כתובת ולחלופין סמן אותו על המפה.</li>
  <li>לחץ <strong>שמור</strong>. המיקום זמין כעת בטופס יצירת האירוע.</li>
</ol>
<ul class="manual-list">
  <li><strong>עריכה / מחיקה</strong> — עדכן או הסר מיקומים קיימים.</li>
  <li><strong>סיכת מפה</strong> — הקואורדינטות משמשות בכפתור ניווט אפליקציית Keyera.</li>
</ul>`,
  },

  'se-clubs-body': {
    en: `<p>Clubs (also called Groups or Departments) are organizational sub-units used to categorize events, passes, and members.</p>
<ul class="manual-list">
  <li><strong>Add Club</strong> — Create a named group with an optional color code and description.</li>
  <li><strong>Assign Events</strong> — Events can be tagged to a club for filtering and reporting.</li>
  <li><strong>Subscriber Groups</strong> — Subscribers can be members of specific clubs.</li>
</ul>`,
    he: `<p>מועדונים (הנקראים גם קבוצות או מחלקות) הם יחידות משנה ארגוניות לסיווג אירועים, תעודות וחברים.</p>
<ul class="manual-list">
  <li><strong>הוספת מועדון</strong> — צור קבוצה בעלת שם עם קוד צבע ותיאור אופציונלי.</li>
  <li><strong>שיוך אירועים</strong> — ניתן לתייג אירועים למועדון לסינון ודיווח.</li>
  <li><strong>קבוצות מנויים</strong> — מנויים יכולים להיות חברים במועדונים ספציפיים.</li>
</ul>`,
  },

  'se-permissions-body': {
    en: `<p>Roles & Permissions defines what each portal user can see and do. Every user has one role, and each role has a <strong>level</strong>.</p>
<ul class="manual-list">
  <li><strong>System roles</strong> — Your organization has its own copy of every system role (Client Admin, Operator…). Changing one affects your organization only; <strong>Reset</strong> returns it to the system default.</li>
  <li><strong>Custom roles</strong> — <strong>New role</strong> creates a role at a level below your own. A custom role can be renamed and deleted once no user has it.</li>
  <li><strong>Editing permissions</strong> — Pick a role, then a module on the left. Switch permissions on or off, or choose None / Read / Write, and press <strong>Save changes</strong>. A switched-off module or group keeps its permissions but grants none of them.</li>
  <li><strong>Who can change what</strong> — You can manage only roles and users below your own level. Your own role and roles at your level or above are read-only, and the page shows only permissions you have yourself. A permission the role has that you do not have stays on the role.</li>
  <li><strong>Export</strong> — Download the selected role's permissions as CSV or JSON.</li>
</ul>`,
    he: `<p>תפקידים והרשאות מגדיר מה כל משתמש פורטל יכול לראות ולעשות. לכל משתמש יש תפקיד אחד, ולכל תפקיד יש <strong>רמה</strong>.</p>
<ul class="manual-list">
  <li><strong>תפקידי מערכת</strong> — לארגון שלך יש עותק משלו של כל תפקיד מערכת (מנהל לקוח, מפעיל…). שינוי בתפקיד כזה משפיע רק על הארגון שלך; <strong>איפוס</strong> מחזיר אותו לברירת המחדל של המערכת.</li>
  <li><strong>תפקידים מותאמים</strong> — <strong>תפקיד חדש</strong> יוצר תפקיד ברמה שמתחת לרמה שלך. אפשר לשנות שם של תפקיד מותאם, ולמחוק אותו כשאף משתמש לא משויך אליו.</li>
  <li><strong>עריכת הרשאות</strong> — בחרו תפקיד ואז מודול מהרשימה בצד. הדליקו או כבו הרשאות, או בחרו ללא / צפייה / עריכה, ולחצו <strong>שמירת שינויים</strong>. מודול או קבוצה כבויים שומרים על ההרשאות שלהם אבל לא מעניקים אף אחת מהן.</li>
  <li><strong>מי יכול לשנות מה</strong> — אפשר לנהל רק תפקידים ומשתמשים שמתחת לרמה שלך. התפקיד שלך ותפקידים ברמה שלך או מעליה מוצגים לצפייה בלבד, והדף מציג רק הרשאות שיש לך בעצמך. הרשאה שיש לתפקיד ואין לך נשארת בתפקיד.</li>
  <li><strong>ייצוא</strong> — הורדת ההרשאות של התפקיד שנבחר כקובץ CSV או JSON.</li>
</ul>`,
  },

  'se-requirements-body': {
    en: `<p>Requirements define mandatory fields or conditions for pass issuance — to enforce data quality or compliance rules.</p>
<ul class="manual-list">
  <li><strong>Mandatory Fields</strong> — Specify which pass fields must be filled before a pass can be issued.</li>
  <li><strong>Document Requirements</strong> — Require the upload of a specific document type before issuance.</li>
  <li><strong>Per-Category Rules</strong> — Different requirements for employees vs. visitors vs. contractors.</li>
</ul>`,
    he: `<p>דרישות מגדירות שדות חובה או תנאים להנפקת תעודות — לאכיפת איכות נתונים או כללי ציות.</p>
<ul class="manual-list">
  <li><strong>שדות חובה</strong> — ציין אילו שדות תעודה חייבים להיות מלאים לפני הנפקת תעודה.</li>
  <li><strong>דרישות מסמכים</strong> — דרוש העלאת סוג מסמך ספציפי לפני ההנפקה.</li>
  <li><strong>כללים לפי קטגוריה</strong> — דרישות שונות לעובדים לעומת מבקרים לעומת קבלנים.</li>
</ul>`,
  },

  'se-uploads-body': {
    en: `<p>The Uploads section provides bulk data import functionality — add hundreds of records at once via Excel.</p>
<ol class="manual-steps">
  <li>Download the appropriate template from <strong>Help Center &gt; Files</strong>.</li>
  <li>Fill in the template with your data (follow the column format exactly).</li>
  <li>Navigate to <strong>Settings &gt; Uploads</strong>.</li>
  <li>Select the import type (Authorized Passes, Employees, etc.).</li>
  <li>Upload your completed Excel file.</li>
  <li>Review the validation summary — errors are highlighted with row-level descriptions.</li>
  <li>Confirm to import valid records. Invalid rows are skipped and can be corrected and re-uploaded.</li>
</ol>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>Tip:</strong> Always use the official template. Do not add, remove, or rename columns.</span></div>`,
    he: `<p>סעיף ההעלאות מספק פונקציונליות ייבוא נתונים בצובר — הוסף מאות רשומות בבת אחת דרך אקסל.</p>
<ol class="manual-steps">
  <li>הורד את התבנית המתאימה ממרכז עזרה &gt; קבצים.</li>
  <li>מלא את התבנית בנתוניך (עקוב אחר פורמט העמודות בדיוק).</li>
  <li>נווט ל<strong>הגדרות &gt; העלאות</strong>.</li>
  <li>בחר את סוג הייבוא (תעודות מורשים, עובדים, וכד').</li>
  <li>העלה את קובץ האקסל המושלם שלך.</li>
  <li>בדוק את סיכום האימות — שגיאות מודגשות עם תיאורים ברמת שורה.</li>
  <li>אשר לייבא רשומות תקפות. שורות לא תקפות מדולגות וניתן לתקן ולהעלות מחדש.</li>
</ol>
<div class="manual-tip"><i class="pi pi-info-circle"></i><span><strong>טיפ:</strong> השתמש תמיד בתבנית הרשמית. אל תוסיף, תסיר או תשנה שם לעמודות.</span></div>`,
  },

  'se-access-body': {
    en: `<p>The Access Control Settings section configures the behavior of the gate access system for different person categories.</p>
<ul class="manual-list">
  <li><strong>Employee Settings</strong> — Define default area access, time rules, and security level for employee-type passes.</li>
  <li><strong>Authorized Settings</strong> — Configure defaults for visitor/authorized person passes.</li>
  <li><strong>Vehicle Settings</strong> — Set rules for vehicle passes (gate types, allowed areas).</li>
  <li><strong>HME Settings</strong> — Configure default speaker groups and announcement behavior.</li>
  <li><strong>Global Defaults</strong> — Settings here apply when specific overrides are not set on individual passes.</li>
</ul>`,
    he: `<p>סעיף הגדרות בקרת גישה מגדיר את התנהגות מערכת הגישה לשערים עבור קטגוריות שונות של אנשים.</p>
<ul class="manual-list">
  <li><strong>הגדרות עובד</strong> — הגדר גישת אזור ברירת מחדל, כללי זמן ורמת אבטחה לתעודות מסוג עובד.</li>
  <li><strong>הגדרות מורשים</strong> — הגדר ברירות מחדל לתעודות מבקרים/אנשים מורשים.</li>
  <li><strong>הגדרות רכב</strong> — הגדר כללים לתעודות רכב (סוגי שערים, אזורים מורשים).</li>
  <li><strong>הגדרות HME</strong> — הגדר קבוצות רמקולים ברירת מחדל והתנהגות הודעות.</li>
  <li><strong>ברירות מחדל גלובליות</strong> — הגדרות אלה חלות כאשר לא הוגדרו עקיפות ספציפיות על תעודות בודדות.</li>
</ul>`,
  },

  'se-station-body': {
    en: `<p>Station Call Groups define logical groups of HME devices for targeted audio announcements.</p>
<ul class="manual-list">
  <li><strong>Create Group</strong> — Name a group and assign HME devices to it.</li>
  <li><strong>Nested Groups</strong> — A group can include individual devices or other groups.</li>
  <li><strong>Use in Broadcasts</strong> — When sending a message in the HME section, select a station group as the target.</li>
  <li><strong>Default Group</strong> — Mark one group as the default for quick announcements.</li>
</ul>`,
    he: `<p>קבוצות תחנת קריאה מגדירות קבוצות לוגיות של התקני HME להודעות קוליות ממוקדות.</p>
<ul class="manual-list">
  <li><strong>יצירת קבוצה</strong> — שם קבוצה והקצה אליה התקני HME.</li>
  <li><strong>קבוצות מקוננות</strong> — קבוצה יכולה לכלול התקנים בודדים או קבוצות אחרות.</li>
  <li><strong>שימוש בשידורים</strong> — בעת שליחת הודעה בסעיף HME, בחר קבוצת תחנה כיעד.</li>
  <li><strong>קבוצת ברירת מחדל</strong> — סמן קבוצה אחת כברירת מחדל להודעות מהירות.</li>
</ul>`,
  },
}
